export type PaginationItem = number | 'ellipsis';

/** The current page carries siblings on both sides. */
const SIBLING_SIDES = 2;
/** A pinned boundary page plus its adjacent ellipsis slot. */
const EDGE_PLUS_ELLIPSIS = 2;

function range(start: number, end: number): number[] {
  const length = Math.max(end - start + 1, 0);
  return Array.from({ length }, (_, i) => start + i);
}

/**
 * Build the visible page list with ellipses, following the Untitled UI / MUI pattern:
 * `boundaryCount` pages pinned at each end, `siblingCount` pages either side of the current page,
 * and `…` where the run is broken. A gap of exactly one page renders that page instead of an
 * ellipsis. Returns 1-based page numbers interleaved with `'ellipsis'` markers.
 */
export function getPaginationRange(
  page: number,
  total: number,
  siblingCount = 1,
  boundaryCount = 1,
): PaginationItem[] {
  if (total <= 0) return [];

  const startPages = range(1, Math.min(boundaryCount, total));
  const endPages = range(Math.max(total - boundaryCount + 1, boundaryCount + 1), total);

  const siblingsStart = Math.max(
    Math.min(page - siblingCount, total - boundaryCount - siblingCount * SIBLING_SIDES - 1),
    boundaryCount + EDGE_PLUS_ELLIPSIS,
  );
  const siblingsEnd = Math.min(
    Math.max(
      page + siblingCount,
      boundaryCount + siblingCount * SIBLING_SIDES + EDGE_PLUS_ELLIPSIS,
    ),
    (endPages[0] ?? total) - EDGE_PLUS_ELLIPSIS,
  );

  return [
    ...startPages,
    ...siblingsStartBreak(siblingsStart, boundaryCount, total),
    ...range(siblingsStart, siblingsEnd),
    ...siblingsEndBreak(siblingsEnd, boundaryCount, total),
    ...endPages,
  ];
}

function siblingsStartBreak(
  siblingsStart: number,
  boundaryCount: number,
  total: number,
): PaginationItem[] {
  if (siblingsStart > boundaryCount + EDGE_PLUS_ELLIPSIS) return ['ellipsis'];
  if (boundaryCount + 1 < total - boundaryCount) return [boundaryCount + 1];
  return [];
}

function siblingsEndBreak(
  siblingsEnd: number,
  boundaryCount: number,
  total: number,
): PaginationItem[] {
  if (siblingsEnd < total - boundaryCount - 1) return ['ellipsis'];
  if (total - boundaryCount > boundaryCount) return [total - boundaryCount];
  return [];
}
